import api from './client';
import type {
  BandmatesResponse,
  ChatConversationSummary,
  ChatMessagesResponse,
  ChatMessage,
  CreateDMResponse,
  CreateGroupResponse,
  ChatConversationMember,
  ChatAiSuggestion,
  ChatAiSuggestionsResponse,
  MentionSearchResponse,
} from '../types';

// ---------------------------------------------------------------------------
// Bandmates (unchanged — sidebar data source)
// ---------------------------------------------------------------------------

/**
 * Fetch all project members the current user can chat with, grouped by project.
 * Includes online status and unread count per person.
 */
export async function getBandmates(): Promise<BandmatesResponse> {
  const { data } = await api.get<BandmatesResponse>('/chat/bandmates');
  return data;
}

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

/**
 * Find or create a DM conversation with another user. Returns the conversationId.
 */
export async function findOrCreateDM(otherUserId: string): Promise<CreateDMResponse> {
  const { data } = await api.post<CreateDMResponse>('/chat/conversations/dm', { otherUserId });
  return data;
}

/**
 * Create a new group conversation within a project.
 */
export async function createGroup(
  projectId: string,
  memberIds: string[],
  name?: string,
): Promise<CreateGroupResponse> {
  const { data } = await api.post<CreateGroupResponse>('/chat/conversations/group', {
    projectId,
    memberIds,
    name,
  });
  return data;
}

/**
 * Fetch all conversations the current user participates in.
 */
export async function getConversations(): Promise<ChatConversationSummary[]> {
  const { data } = await api.get<{ conversations: ChatConversationSummary[] }>('/chat/conversations');
  return data.conversations;
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

/**
 * Fetch messages for a conversation.
 * Pass `before` (ISO date string) for cursor-based pagination.
 */
export async function getMessages(
  conversationId: string,
  options?: { limit?: number; before?: string },
): Promise<ChatMessagesResponse> {
  const { data } = await api.get<ChatMessagesResponse>(
    `/chat/conversations/${conversationId}/messages`,
    { params: options },
  );
  return data;
}

/**
 * Send a message to a conversation.
 */
export async function sendMessage(conversationId: string, content: string): Promise<ChatMessage> {
  const { data } = await api.post<ChatMessage>(
    `/chat/conversations/${conversationId}/messages`,
    { content },
  );
  return data;
}

/**
 * Mark all unread messages in a conversation as read.
 */
export async function markConversationRead(conversationId: string): Promise<{ count: number }> {
  const { data } = await api.patch<{ count: number }>(
    `/chat/conversations/${conversationId}/read`,
  );
  return data;
}

/**
 * Edit a sent message. Only the sender can edit their own messages.
 */
export async function editMessage(messageId: string, content: string): Promise<ChatMessage> {
  const { data } = await api.put<ChatMessage>(`/chat/messages/${messageId}`, { content });
  return data;
}

/**
 * Delete a sent message. Only the sender can delete their own messages.
 */
export async function deleteMessage(messageId: string): Promise<void> {
  await api.delete(`/chat/messages/${messageId}`);
}

// ---------------------------------------------------------------------------
// Reactions
// ---------------------------------------------------------------------------

/**
 * Add an emoji reaction to a message.
 */
export async function addReaction(messageId: string, emoji: string): Promise<void> {
  await api.post(`/chat/messages/${messageId}/reactions`, { emoji });
}

/**
 * Remove an emoji reaction from a message.
 */
export async function removeReaction(messageId: string, emoji: string): Promise<void> {
  const encodedEmoji = encodeURIComponent(emoji);
  await api.delete(`/chat/messages/${messageId}/reactions/${encodedEmoji}`);
}

// ---------------------------------------------------------------------------
// Group management
// ---------------------------------------------------------------------------

/**
 * Rename a group conversation. Any member can rename.
 */
export async function renameConversation(
  conversationId: string,
  name: string,
): Promise<{ conversationId: string; name: string }> {
  const { data } = await api.patch<{ conversationId: string; name: string }>(
    `/chat/conversations/${conversationId}/name`,
    { name },
  );
  return data;
}

/**
 * Add members to a group conversation.
 */
export async function addMembers(
  conversationId: string,
  memberIds: string[],
): Promise<{ added: ChatConversationMember[] }> {
  const { data } = await api.post<{ added: ChatConversationMember[] }>(
    `/chat/conversations/${conversationId}/members`,
    { memberIds },
  );
  return data;
}

/**
 * Remove a member from a group conversation (or leave if removing self).
 */
export async function removeMember(
  conversationId: string,
  memberId: string,
): Promise<{ success: boolean }> {
  const { data } = await api.delete<{ success: boolean }>(
    `/chat/conversations/${conversationId}/members/${memberId}`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Email preferences (unchanged)
// ---------------------------------------------------------------------------

export async function getChatEmailPreference(): Promise<{ optedOut: boolean }> {
  const { data } = await api.get<{ optedOut: boolean }>('/chat/email-preference');
  return data;
}

export async function setChatEmailPreference(optedOut: boolean): Promise<{ optedOut: boolean }> {
  const { data } = await api.put<{ optedOut: boolean }>('/chat/email-preference', { optedOut });
  return data;
}

// ---------------------------------------------------------------------------
// AI suggestions
// ---------------------------------------------------------------------------

export async function getAiSuggestions(conversationId: string): Promise<ChatAiSuggestion[]> {
  const { data } = await api.get<ChatAiSuggestionsResponse>(`/chat/conversations/${conversationId}/ai-suggestions`);
  return data.suggestions;
}

export async function dismissAiSuggestion(
  suggestionId: string,
  options?: { sendFeedback?: boolean }
): Promise<{ success: boolean; suggestionId: string; status: string }> {
  const sendFeedback = options?.sendFeedback ?? false;
  const { data } = await api.post<{ success: boolean; suggestionId: string; status: string }>(
    `/chat/ai-suggestions/${suggestionId}/dismiss${sendFeedback ? '?feedback=true' : ''}`
  );
  return data;
}

export interface AcceptAiSuggestionInput {
  cutId: string;
  managedFileId?: string;
  timestamp?: number;
  content?: string;
  targetUserId?: string;
}

export interface AcceptAiSuggestionResponse {
  success: boolean;
  suggestionId: string;
  status: string;
  comment: {
    id: string;
    cutId: string;
    managedFileId: string;
    timestamp: number | null;
    content: string;
  } | null;
  approvalRequest: {
    id: string;
    status: string;
  } | null;
}

export interface TriggerAiSuggestionResponse {
  status: 'CREATED' | 'EXISTING';
  suggestion: ChatAiSuggestion;
}

export async function acceptAiSuggestion(
  suggestionId: string,
  input: AcceptAiSuggestionInput,
): Promise<AcceptAiSuggestionResponse> {
  const { data } = await api.post<AcceptAiSuggestionResponse>(`/chat/ai-suggestions/${suggestionId}/accept`, input);
  return data;
}

export async function triggerAiSuggestion(messageId: string): Promise<TriggerAiSuggestionResponse> {
  const { data } = await api.post<TriggerAiSuggestionResponse>(`/chat/messages/${messageId}/ai-suggest`);
  return data;
}

export async function mentionSearch(conversationId: string, q: string): Promise<MentionSearchResponse> {
  const { data } = await api.get<MentionSearchResponse>(`/chat/conversations/${conversationId}/mention-search`, {
    params: { q },
  });
  return data;
}
